import { useState, useEffect, useRef, useCallback } from 'react';
import type { Editor } from '@tiptap/core';
import {
  findParentMark,
  isMovingToHoverCard,
  computeMarkHoverPosition,
  MARK_HOVER_CLOSE_DELAY_MS,
} from '../utils/mark-hover-utils.js';

type UseMarkHoverOptions<T extends { id: string }> = {
  editor: Editor | null;
  items: T[];
  markDataAttribute: string;
  isMobile: boolean;
  closeDelayMs?: number;
};

type UseMarkHoverReturn<T extends { id: string }> = {
  hoveredId: string | null;
  hoveredItem: T | null;
  hoveredPosition: { top: number; left: number } | null;
  hoverCardContentRef: React.RefObject<HTMLDivElement>;
  setHoveredItem: (item: T | null) => void;
  closeHover: () => void;
  setKeepOpen: (keepOpen: boolean) => void;
  onHoverCardMouseEnter: () => void;
  onHoverCardMouseLeave: () => void;
};

export function useMarkHover<T extends { id: string }>({
  editor,
  items,
  markDataAttribute,
  isMobile,
  closeDelayMs = MARK_HOVER_CLOSE_DELAY_MS,
}: UseMarkHoverOptions<T>): UseMarkHoverReturn<T> {
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [hoveredItem, setHoveredItem] = useState<T | null>(null);
  const [hoveredPosition, setHoveredPosition] = useState<{ top: number; left: number } | null>(null);
  const hoverCardContentRef = useRef<HTMLDivElement | null>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isOverMarkRef = useRef(false);
  const isOverCardRef = useRef(false);
  const keepOpenRef = useRef(false);
  const hoveredIdRef = useRef<string | null>(null);

  hoveredIdRef.current = hoveredId;

  const closeHover = useCallback(() => {
    keepOpenRef.current = false;
    setHoveredId(null);
    setHoveredItem(null);
    setHoveredPosition(null);
    isOverMarkRef.current = false;
    isOverCardRef.current = false;
  }, []);

  const cancelClose = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimeoutRef.current = setTimeout(() => {
      if (keepOpenRef.current) {
        return;
      }
      if (!isOverMarkRef.current && !isOverCardRef.current) {
        closeHover();
      }
    }, closeDelayMs);
  }, [cancelClose, closeHover, closeDelayMs]);

  const setKeepOpen = useCallback((keepOpen: boolean) => {
    keepOpenRef.current = keepOpen;
    if (keepOpen) {
      cancelClose();
    } else {
      scheduleClose();
    }
  }, [cancelClose, scheduleClose]);

  const onHoverCardMouseEnter = useCallback(() => {
    isOverCardRef.current = true;
    cancelClose();
  }, [cancelClose]);

  const onHoverCardMouseLeave = useCallback(() => {
    isOverCardRef.current = false;
    scheduleClose();
  }, [scheduleClose]);

  const openHover = useCallback((item: T, mark: HTMLElement) => {
    if (keepOpenRef.current && hoveredIdRef.current && hoveredIdRef.current !== item.id) {
      return;
    }
    cancelClose();
    isOverMarkRef.current = true;
    const rect = mark.getBoundingClientRect();
    setHoveredId(item.id);
    setHoveredItem(item);
    setHoveredPosition(computeMarkHoverPosition(rect));
  }, [cancelClose]);

  useEffect(() => {
    if (!editor) {
      return;
    }

    let editorElement: HTMLElement | null = null;
    try {
      editorElement = editor.view.dom;
    } catch {
      return;
    }
    if (!editorElement) {
      return;
    }

    const handleMouseOver = (e: MouseEvent): void => {
      if (isMobile) {
        return;
      }
      const target = e.target as HTMLElement;
      const mark = findParentMark(target, editorElement!, markDataAttribute);
      if (!mark) {
        return;
      }

      const itemId = mark.getAttribute(markDataAttribute);
      if (!itemId) {
        return;
      }

      const item = items.find((currentItem) => currentItem.id === itemId);
      if (item) {
        openHover(item, mark);
      }
    };

    const handleMouseOut = (e: MouseEvent): void => {
      if (isMobile) {
        return;
      }
      const target = e.target as HTMLElement;
      const relatedTarget = e.relatedTarget as HTMLElement | null;
      const mark = findParentMark(target, editorElement!, markDataAttribute);
      if (!mark) {
        return;
      }

      const itemId = mark.getAttribute(markDataAttribute);
      if (!itemId) {
        return;
      }

      const relatedMark = findParentMark(relatedTarget, editorElement!, markDataAttribute);
      const movingToHoverCard = isMovingToHoverCard(relatedTarget, hoverCardContentRef.current);

      if (relatedMark?.getAttribute(markDataAttribute) === itemId) {
        return;
      }

      isOverMarkRef.current = false;

      if (!movingToHoverCard) {
        scheduleClose();
      }
    };

    const handleClick = (e: MouseEvent): void => {
      const target = e.target as HTMLElement;
      const mark = findParentMark(target, editorElement!, markDataAttribute);
      if (!mark || !isMobile) {
        return;
      }

      const itemId = mark.getAttribute(markDataAttribute);
      if (!itemId) {
        return;
      }

      const item = items.find((currentItem) => currentItem.id === itemId);
      if (item) {
        e.preventDefault();
        setHoveredId(itemId);
        setHoveredItem(item);
        setHoveredPosition({ top: 0, left: 0 });
      }
    };

    const handleTouchStart = (e: TouchEvent): void => {
      if (!isMobile) {
        return;
      }
      const target = e.target as HTMLElement;
      const mark = findParentMark(target, editorElement!, markDataAttribute);
      if (!mark) {
        return;
      }

      const itemId = mark.getAttribute(markDataAttribute);
      if (!itemId) {
        return;
      }

      const item = items.find((currentItem) => currentItem.id === itemId);
      if (item) {
        e.preventDefault();
        setHoveredId(itemId);
        setHoveredItem(item);
        setHoveredPosition({ top: 0, left: 0 });
      }
    };

    editorElement.addEventListener('mouseover', handleMouseOver);
    editorElement.addEventListener('mouseout', handleMouseOut);
    editorElement.addEventListener('click', handleClick);
    if (isMobile) {
      editorElement.addEventListener('touchstart', handleTouchStart, { passive: false });
    }

    return () => {
      editorElement.removeEventListener('mouseover', handleMouseOver);
      editorElement.removeEventListener('mouseout', handleMouseOut);
      editorElement.removeEventListener('click', handleClick);
      if (isMobile) {
        editorElement.removeEventListener('touchstart', handleTouchStart);
      }
      cancelClose();
    };
  }, [editor, items, markDataAttribute, isMobile, openHover, scheduleClose, cancelClose]);

  return {
    hoveredId,
    hoveredItem,
    hoveredPosition,
    hoverCardContentRef,
    setHoveredItem,
    closeHover,
    setKeepOpen,
    onHoverCardMouseEnter,
    onHoverCardMouseLeave,
  };
}
